import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CreditCard, CheckCircle, XCircle, Clock, Download } from 'lucide-react';
import { getClientPayments } from '../api';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientPayments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [loading, setLoading] = useState(true);

  const statusColors = {
    paid: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800'
  };

  const statusIcons = {
    paid: <CheckCircle className="h-4 w-4" />,
    pending: <Clock className="h-4 w-4" />,
    failed: <XCircle className="h-4 w-4" />
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/api/client/auth/me`, { credentials: 'include' });
        if (!response.ok) navigate('/portal/login');
      } catch { navigate('/portal/login'); }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const loadPayments = async () => {
      try {
        const data = await getClientPayments();
        setPayments(data.payments || []);
        setTotalPaid(data.total_paid || 0);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    loadPayments();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleDownloadReceipt = async (bookingId) => {
    try {
      const response = await fetch(`${API}/api/client/portal/receipt/${bookingId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch receipt');
      const data = await response.json();
      const receipt = data.receipt;

      // Generate printable receipt
      const receiptWindow = window.open('', '_blank');
      receiptWindow.document.write(`
        <html><head><title>ACE Receipt - ${receipt.booking_id}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 600px; margin: 40px auto; padding: 20px; color: #333; }
          .header { text-align: center; border-bottom: 2px solid #dc2626; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; margin: 0; } .header p { color: #666; margin: 5px 0 0; }
          .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
          .label { color: #666; } .value { font-weight: bold; }
          .total { font-size: 24px; text-align: center; margin: 20px 0; padding: 20px; background: #f9fafb; border-radius: 8px; }
          .footer { text-align: center; color: #999; font-size: 12px; margin-top: 40px; }
          @media print { body { margin: 0; } }
        </style></head><body>
        <div class="header"><h1>Auto Concierge Enterprises</h1><p>Receipt / Invoice</p></div>
        <div class="row"><span class="label">Booking ID</span><span class="value">${receipt.booking_id}</span></div>
        <div class="row"><span class="label">Service</span><span class="value">${receipt.service_type}</span></div>
        <div class="row"><span class="label">Date</span><span class="value">${receipt.pickup_date ? new Date(receipt.pickup_date).toLocaleDateString() : 'N/A'}</span></div>
        <div class="row"><span class="label">Time</span><span class="value">${receipt.pickup_time || 'N/A'}</span></div>
        <div class="row"><span class="label">Pickup</span><span class="value">${receipt.pickup_location}</span></div>
        <div class="row"><span class="label">Drop-off</span><span class="value">${receipt.dropoff_location}</span></div>
        <div class="row"><span class="label">Hours</span><span class="value">${receipt.hours}</span></div>
        <div class="row"><span class="label">Status</span><span class="value">${receipt.status}</span></div>
        <div class="row"><span class="label">Payment Status</span><span class="value">${receipt.payment_status}</span></div>
        <div class="total"><div style="color:#666;font-size:14px;">Total</div><div style="color:#dc2626;">$${receipt.total_price}</div></div>
        <div class="footer"><p>Auto Concierge Enterprises LLC | Sandy Springs, GA 30350</p><p>(678) 846-3009 | JD@YourAutoConcierge.com</p><p>Thank you for choosing ACE.</p></div>
        </body></html>
      `);
      receiptWindow.document.close();
    } catch (err) {
      console.error('Receipt error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="client-payments-page">
      <header className="bg-black text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/portal')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <h1 className="text-lg sm:text-xl font-bold">Payment History</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Summary */}
        <Card className="p-5 sm:p-6 mb-6 border-gray-200 bg-gradient-to-r from-green-600 to-green-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm mb-1">Total Paid</p>
              <p className="text-3xl sm:text-4xl font-bold">${totalPaid.toFixed(2)}</p>
            </div>
            <div className="h-14 w-14 bg-white/20 rounded-full flex items-center justify-center">
              <CreditCard className="h-7 w-7" />
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto" /></div>
        ) : payments.length === 0 ? (
          <Card className="p-10 sm:p-12 text-center border-gray-200">
            <CreditCard className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-black mb-2">No payments yet</h3>
            <p className="text-gray-600">Your payment history will appear here after bookings.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {payments.map((payment, idx) => (
              <Card key={idx} className="p-4 sm:p-6 border-gray-200" data-testid={`payment-card-${idx}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      payment.payment_status === 'paid' ? 'bg-green-100' : payment.payment_status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                    }`}>
                      {statusIcons[payment.payment_status] || statusIcons.pending}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-mono text-gray-500 truncate">{payment.payment_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[payment.payment_status] || 'bg-gray-100'}`}>
                          {payment.payment_status}
                        </span>
                      </div>
                      <p className="font-semibold text-black text-sm">{payment.service_type || 'Booking Deposit'}</p>
                      <p className="text-xs text-gray-500">{formatDate(payment.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2 flex-shrink-0">
                    <p className="text-xl sm:text-2xl font-bold text-black">${payment.amount?.toFixed(2)}</p>
                    <div className="flex items-center gap-2">
                      {payment.booking_id && (
                        <Button
                          onClick={() => handleDownloadReceipt(payment.booking_id)}
                          size="sm"
                          variant="outline"
                          className="border-gray-300 text-gray-600 hover:bg-black hover:text-white text-xs"
                          data-testid={`receipt-btn-${idx}`}
                        >
                          <Download className="h-3 w-3 mr-1" /> Receipt
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPayments;
